import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { CancellationState, PaymentClient, PaymentProvider, PaymentState } from "@/data/model/Enums";
import type { ITransactionMethodSplit } from "@/data/model/Order";
import { config, role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";

import { i18n, isInRole } from "@/portal/Trans";
import { DataTable, type IColumnMapping, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { TastierGlobal } from "@/portal/globalAccess";
import { TransactionDetailsWindow } from "@/portal/modal/TransactionDetailsWindow";
import { TransactionExportWindow } from "@/portal/modal/TransactionExportWindow";

export class TransactionTableController {
    private $element: JQuery;
    private dao: OrderDao;
    private dataTable: DataTable<ITransactionMethodSplit>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<OrderDao>(OrderDao);

        //bind export btn
        if (isInRole(role.Admin)) {
            this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.export").click((e) => {
                e.preventDefault();
                e.stopPropagation();

                new TransactionExportWindow().show();
            });
        }

        const tableConfig: IDataTableConfig<ITransactionMethodSplit> = {
            columnMapping: this.getColumns(),
            defaultSortAsc: false,
            locationDependend: !isInRole(role.Admin),
            defaultSortProperty: "confirmDate",
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromEnum(
            "state",
            PaymentState,
            "enums.PaymentState",
            i18n("transaction.allStates")
        );

        if (isInRole(role.Admin)) {
            this.search.addDropdownFilter(
                "locationId",
                Factory.getInstance<LocationDao>(LocationDao),
                false,
                i18n("transaction.allLocations"),
                undefined,
                (m) => {
                    if (m.internalName != null && m.internalName.length > 0) {
                        return m.internalName;
                    } else {
                        return m.name;
                    }
                }
            );
        }

        this.dataTable = new DataTable<ITransactionMethodSplit>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            {
                getData: (s, abortSignal) => {
                    if (!isInRole(role.Admin)) {
                        return this.dao.getTransactionMethodSplits({
                            locationId: TastierGlobal.getLocationSwitch()!.getCurrentLocationId(),
                            state: s["state"],
                            term: s["term"],
                            orderBy: s["orderBy"],
                            isAscending: s["isAscending"],
                            limit: s["limit"],
                            offset: s["offset"],
                        }, abortSignal);
                    } else {
                        return this.dao.getTransactionMethodSplits(s, abortSignal);
                    }
                },
            },
            tableConfig
        );
        this.dataTable.listenTo(this.search);
        this.dataTable.onClick(
            (transaction) => {
                this.openTransactionWindow(transaction.paymentId);
            },
            (transaction) => {
                this.openTransactionWindow(transaction.paymentId);
            }
        );

        if (!isInRole(role.Admin)) {
            portalEvents.OnLocationChanged.add(() => this.dataTable.loadData(true), this);
        }
    }

    private openTransactionWindow(transactionId: string) {
        const window = new TransactionDetailsWindow(transactionId);
        window.show();
    }

    private getColumns(): IColumnMapping<ITransactionMethodSplit>[] {
        let columns: IColumnMapping<ITransactionMethodSplit>[] = [
            {
                name: i18n("general.fields.date"),
                property: "confirmDate",
                styles: { width: "9%" },
                formatter: (m, p) =>
                    p == null
                        ? DateTime.toPreciseDateTimeString(m.createdDateTime)
                        : DateTime.toPreciseDateTimeString(p),
            },
            {
                name: i18n("general.fields.state"),
                property: "state",
                styles: { width: "7%" },
                sortable: false,
                formatter: (m, p) => {
                    let styleClass = "kt-badge--primary";
                    switch (p) {
                        case PaymentState.Intented:
                            styleClass = "kt-badge--dark";
                            break;
                        case PaymentState.Confirmed:
                            styleClass = "kt-badge--success";
                            break;
                        case PaymentState.Declined:
                            styleClass = "kt-badge--danger";
                            break;
                    }

                    return (
                        '<div class="kt-badge ' +
                        styleClass +
                        ' kt-badge--inline" ' +
                        (m.declineReason !== undefined ? 'title="' + m.declineReason + '"' : "") +
                        ">" +
                        i18n("enums.PaymentState." + PaymentState[p]) +
                        "</div>"
                    );
                },
            },
            {
                name: i18n("order.fields.cancellation"),
                property: "cancellationState",
                styles: { width: "9%" },
                sortable: false,
                formatter: (m, p) => {
                    if (p === undefined) return "";

                    return (
                        '<div class="kt-badge kt-badge--danger kt-badge--inline">' +
                        i18n("enums.CancellationState." + CancellationState[p]) +
                        "</div>"
                    );
                },
            },
        ];

        if (isInRole(role.Admin)) {
            columns.push({
                name: i18n("general.entities.location"),
                property: "locationName",
                sortable: false,
                styles: { width: "10%" },
                formatter: (m) => {
                    return $(
                        '<a href="/location/' + m.locationId + '">' + Format.htmlEscape(m.locationInternalName != null && m.locationInternalName.length > 0 ? m.locationInternalName : m.locationName) + "</a>"
                    ).click((e) => {
                        e.stopPropagation();
                    });
                },
            });
        }

        columns = columns.concat([
            {
                name: i18n("general.entities.table"),
                property: "orderId",
                sortable: false,
                styles: { width: "8%" },
                formatter: (m) => {
                    if (m.tableId == null) return i18n("order.deletedTable");
                    return  $("<div>" + Format.htmlEscape(m.tableName) + "</div>");
                },
            },
            {
                name: i18n("general.entities.orders"),
                property: "orderId",
                sortable: false,
                styles: { width: "8%" },
                formatter: (m) => {
                    const $container = $('<div style="display: flex; flex-direction: column;"></div>');
                    m.transactionOrders.forEach((ord) => {
                        const $order = $(`<a href="/order/${ord.orderId}?lId=${ord.locationId}" style="margin-right: 4px;">${ord.orderNumber}</a>`).appendTo($container);

                        $order.click((e) => {
                            e.stopPropagation();
                        });
                    });

                    if (m.transactionOrders.length === 0) return i18n("general.deletedOrder");

                    return $container;
                },
            },
            {
                name: i18n("order.fields.totalIncTip"),
                property: "amount",
                sortable: false,
                styles: { width: "7%", "text-align": "right" },
                formatter: (m, p) => Format.toMoneyFormat(p),
            },
        ]);

        if (isInRole(role.Admin)) {
            columns = columns.concat([
                {
                    name: i18n("transaction.fields.provider"),
                    property: "provider",
                    sortable: false,
                    styles: { width: "7%" },
                    formatter: (m, p) => {
                        switch (p) {
                            case PaymentProvider.Stripe:
                                return $(
                                    '<a target="_blank" href="' +
                                        config.stripeUrl +
                                        "payments/" +
                                        m.providerId +
                                        '">Stripe</a>'
                                ).click((e) => {
                                    e.stopPropagation();
                                });
                            case PaymentProvider.Wallee:
                                return $(
                                    '<a target="_blank" href="' +
                                        (m.merchantId == null || m.merchantId === ""
                                            ? config.walleeUrlOld
                                            : config.walleeUrl + m.merchantId + "/") +
                                        "payment/transaction/view/" +
                                        m.providerId +
                                        '">Wallee</a>'
                                ).click((e) => {
                                    e.stopPropagation();
                                });
                            case PaymentProvider.PostFinance:
                                return $(
                                    '<a target="_blank" href="' +
                                        (config.postFinanceUrl + m.merchantId + "/") +
                                        "payment/transaction/view/" +
                                        m.providerId +
                                        '">PostFinance</a>'
                                ).click((e) => {
                                    e.stopPropagation();
                                });
                        }

                        if (p == null) {
                            return i18n("enums.RefundMode.Offline");
                        } else {
                            return i18n("enums.PaymentProvider." + PaymentProvider[p]);
                        }
                    },
                },
                {
                    name: i18n("transaction.fields.client"),
                    property: "client",
                    sortable: false,
                    styles: { width: "6%" },
                    formatter: (m, p) => {
                        if (p === undefined || p == null) {
                            return "";
                        } else {
                            return i18n("enums.PaymentClient." + PaymentClient[p]);
                        }
                    },
                },
                {
                    name: i18n("transaction.fields.paymentMethodButton"),
                    property: "provider",
                    sortable: false,
                    styles: { width: "7%" },
                    formatter: (m) => {
                        if (m.code === "Voucher" || m.isDiscountPayment) {
                            return `<div class="pm-button"><span class="brandicon" style="background-image:url('/assets/img/brands/percent.svg');"></span></div>`;
                        } else if (
                            m.customPaymentMethodImagePath != null &&
                            m.customPaymentMethodImagePath.length > 0
                        ) {
                            return `<div class="pm-button"><span class="brandicon" style="background-image:url('${m.customPaymentMethodImagePath}');"></span></div>`;
                        } else if (
                            m.predefinedPaymentMethodImagePath != null &&
                            m.predefinedPaymentMethodImagePath.length > 0
                        ) {
                            if (m.predefinedPaymentMethodImagePath === "collective_icon") {
                                return '<div class="icon"><div class="visa"></div><div class="mastercard"></div><div class="plus"><i class="fas fa-plus"></i></div></div>';
                            } else {
                                return `<div class="pm-button"><span class="brandicon" style="background-image:url('${m.predefinedPaymentMethodImagePath}');"></span></div>`;
                            }
                        }
                    },
                },
            ]);
        }

        columns = columns.concat([
            {
                name: i18n("transaction.fields.paymentMethod"),
                property: "provider",
                styles: { width: "11%" },
                sortable: false,
                formatter: (m) => {
                    return PaymentMethodUtil.generatePaymentMethodSplitDivs("", [m]).join("");
                },
            },
            {
                name: i18n("general.entities.user"),
                property: "userName",
                sortable: false,
                styles: { width: "11%" },
                formatter: (m) => {
                    if (m.identityId == null) {
                        return m.userName === undefined ? i18n("general.deletedUser") : Format.htmlEscape(m.userName);
                    }
                    
                    if (isInRole(role.Admin)) {
                        if (m.userName === "Anonymer User") {
                            return $(`<a href="/user/${m.identityId}">${i18n("general.anonymousUser")}</a>`).click((e) => {
                                e.stopPropagation();
                            });
                        }

                        return $(`<a href="/user/${m.identityId}">${m.userName === undefined || m.userName === "" ? i18n("general.anonymousUser") : m.userName}</a>`).click((e) => {
                            e.stopPropagation();
                        });
                    } else {
                        if (m.userName === "Anonymer User") {
                            return Format.htmlEscape(i18n("general.anonymousUser"));
                        } else {
                            return m.userName;
                        }
                    }
                },
            },
        ]);

        return columns;
    }
}
